/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { IPaginationCount } from './pagination.dto';
import { CdsLangService, CdsLanguageModule } from '@cds/ng-core/lang';
import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CdsIconModule } from '@cds/ng-web-components/icon';
import { CdsTextfieldModule } from '@cds/ng-web-components/textfield';

@Component({
  selector: 'app-pagination',
  templateUrl: './pagination.component.html',
  styleUrls: ['./pagination.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, FormsModule, CdsIconModule, CdsTextfieldModule, CdsLanguageModule],
})
export class AppPaginationComponent implements AfterViewInit, OnChanges, OnInit {
  private elemRef = inject(ElementRef);
  private cdr = inject(ChangeDetectorRef, { optional: true }) ?? ({ detectChanges: () => undefined } as ChangeDetectorRef);
  langService = inject(CdsLangService);

  private _pageCount: IPaginationCount = { current: 1, count: 1 };

  private _isViewInit = false;
  private inkBarSyncAttempt = 0;
  private readonly maxInkBarSyncAttempts = 4;
  private inkBarRetryTimer?: ReturnType<typeof setTimeout>;

  @ViewChild('inkBar') inkBar?: ElementRef<HTMLElement>;

  @Output() pageChange = new EventEmitter<number>();
  @Output() pageCountChange = new EventEmitter<IPaginationCount>();
  @Input() isShowInput = true;
  @Input() isLoading = false;
  @Input() useOf = false;
  @Input() textfieldAlwaysAvailable = false;
  @Input()
  set pageCount(iPageCount: IPaginationCount) {
    const incomingCount = Number(iPageCount?.count);
    const incomingCurrent = Number(iPageCount?.current);
    const count = Number.isFinite(incomingCount) && incomingCount > 0 ? incomingCount : 1;
    const current = Number.isFinite(incomingCurrent) && incomingCurrent > 0 ? Math.min(incomingCurrent, count) : 1;

    // Never emit pageChange from input synchronization; only user actions should emit.
    this._pageCount = { current, count };
    this.setPageInputValue(current);
    this.updateInkBarPosition();
  }

  get pageCount(): IPaginationCount {
    return this._pageCount;
  }

  pageInputValue = '1';

  lang = 'en';
  ngOnInit(): void {
    this.langService.currentLanguageObservable.subscribe(lang => {
      this.lang = lang;
      this.cdr.detectChanges();
      this.updateInkBarPosition();
    });
  }

  ngAfterViewInit(): void {
    this._isViewInit = true;
    this.updateInkBarPosition();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['pageCount']) {
      this.updateInkBarPosition();
    }
    if (changes['isLoading']) {
      console.debug(`isLoadingisLoadingisLoadingisLoading`);
    }
  }

  updateInkBarPosition() {
    if (!this._isViewInit) {
      return;
    }
    this.inkBarSyncAttempt = 0;
    this.syncInkBarPosition();
  }

  private syncInkBarPosition() {
    requestAnimationFrame(() => {
      const selectedElem = this.getSelectedElem();
      if (!this.inkBar || !selectedElem || selectedElem.offsetWidth <= 0) {
        if (this.inkBarSyncAttempt < this.maxInkBarSyncAttempts) {
          this.inkBarSyncAttempt += 1;
          if (this.inkBarRetryTimer) {
            clearTimeout(this.inkBarRetryTimer);
          }
          this.inkBarRetryTimer = setTimeout(() => this.syncInkBarPosition(), 16);
        }
        return;
      }
      this.inkBar.nativeElement.style.left = selectedElem.offsetLeft + 'px';
      this.inkBar.nativeElement.style.width = selectedElem.offsetWidth + 'px';
    });
  }

  clickPage(page: number) {
    console.debug(`clickPage=${page}`);
    // if (!this.isLoading) {

    // }
    this.selectPage(page);
  }

  selectPage(page: number, event?: any) {
    console.debug(`selectPage=${page}`);

    if (page > this.pageCount.count) {
      page = this.pageCount.count;
    } else if (page < 1) {
      page = 1;
    }
    if (this.pageCount.current === page) {
      this.setPageInputValue(page);
      if (event?.target) {
        event.target.value = page;
      } else if (event?.currentTarget) {
        event.currentTarget.value = page;
      }
      return;
    }
    this.pageCount.current = page;
    this.setPageInputValue(page);
    this.updateInkBarPosition();
    this.pageCountChange.emit({ ...this._pageCount });
    this.pageChange.emit(this._pageCount.current);
  }

  isPageShown(page: number) {
    if (page === 1 || page === this.pageCount.count) {
      return true;
    }
    if (this.pageCount.current - 2 <= page && this.pageCount.current + 2 >= page) {
      return true;
    }
    if (this.pageCount.current - 4 === 1 && page < this.pageCount.count) {
      return 1 < page;
    }
    if (this.pageCount.current + 4 >= this.pageCount.count) {
      return this.pageCount.current < page && page < this.pageCount.count;
    }
    return false;
  }

  getSelectedElem() {
    return this.elemRef.nativeElement.getElementsByClassName('selected')[0];
  }

  private setPageInputValue(value: string | number) {
    this.pageInputValue = String(value);
  }

  gotoPage(value?: string | number | null) {
    const iPage = String(value ?? this.pageInputValue ?? '').trim();

    // If the input is cleared, restore the current page instead of defaulting to page 1.
    if (iPage === '') {
      this.setPageInputValue(this.pageCount.current);
      this.selectPage(this.pageCount.current);
      return;
    }

    const parsedPage = parseInt(iPage, 10);
    let searchPage = parsedPage;
    if (!Number.isFinite(searchPage) || searchPage <= 0) {
      searchPage = 1;
    }
    console.debug(searchPage);
    this.selectPage(searchPage);
  }
}
